import React, { useState } from 'react';
import { MapPin, Target } from 'lucide-react';
import toast from 'react-hot-toast';

const SimpleMapPicker = ({ onLocationSelect }) => {
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser.');
      return;
    }

    setIsDetectingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          // Use a simple reverse geocoding service
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
          );
          const data = await response.json();
          
          let fullAddress = '';
          if (data.localityInfo && data.localityInfo.administrative) {
            const admin = data.localityInfo.administrative;
            fullAddress = `${admin[0]?.name || ''}, ${admin[1]?.name || ''}, ${data.locality || 'Jaipur'}, ${data.countryName || 'India'}`.replace(/^,\s*/, '');
          }
          
          if (!fullAddress) {
            fullAddress = `Location: ${lat.toFixed(4)}, ${lng.toFixed(4)}, Jaipur, India`;
          }

          setAddress(fullAddress);
          setCoordinates({ lat, lng });
          
          if (onLocationSelect) {
            onLocationSelect({
              address: fullAddress,
              lat,
              lng,
              coordinates: { lat, lng }
            });
          }
          
          toast.success('Location detected successfully!');
        } catch (error) {
          console.error('Error getting address:', error);
          toast.error('Could not get address. Please enter manually.');
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsDetectingLocation(false);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location access denied. Please enable location permissions.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information unavailable.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out.');
            break;
          default:
            toast.error('An unknown error occurred while detecting location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  return (
    <div style={{
      width: '100%',
      height: '400px',
      backgroundColor: '#f3f4f6',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Instructions */}
      <div style={{
        position: 'absolute',
        top: '16px',
        left: '16px',
        right: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'center',
        backdropFilter: 'blur(10px)',
        zIndex: 1000
      }}>
        📍 Click "Detect My Location" to get your current address
      </div>

      {/* Map iframe as background */}
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3557.123456789!2d75.7873!3d26.9124!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396c4adf4c57e281%3A0xce1c63a0cf22e09!2sJaipur%2C%20Rajasthan!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
        width="100%"
        height="100%"
        style={{ 
          border: 0, 
          borderRadius: '12px',
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0.3
        }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="Jaipur Map Background"
      />

      {/* Overlay content */}
      <div style={{
        position: 'relative',
        zIndex: 100,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '20px',
        borderRadius: '12px',
        textAlign: 'center',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        {/* Map Icon */}
        <div style={{
          width: '60px',
          height: '60px',
          backgroundColor: '#ff6b35',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px',
          margin: '0 auto 16px'
        }}>
          🗺️
        </div>

        {/* Instructions */}
        <div style={{ textAlign: 'center', color: '#374151', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px', color: '#1f2937' }}>
            Location Detection
          </h3>
          <p style={{ fontSize: '14px', margin: '0 0 16px', color: '#6b7280' }}>
            Click the button below to detect your current location
          </p>
        </div>

        {/* Detect Location Button */}
        <button
          onClick={detectLocation}
          disabled={isDetectingLocation}
          style={{
            padding: '12px 24px',
            backgroundColor: isDetectingLocation ? '#9ca3af' : '#ff6b35',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isDetectingLocation ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '0 auto'
          }}
          onMouseEnter={(e) => {
            if (!isDetectingLocation) {
              e.currentTarget.style.backgroundColor = '#e55a2b';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDetectingLocation) {
              e.currentTarget.style.backgroundColor = '#ff6b35';
            }
          }}
        >
          {isDetectingLocation ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #f3f4f6',
                borderTop: '2px solid #ff6b35',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Detecting...
            </>
          ) : (
            <>
              <Target size={16} />
              Detect My Location
            </>
          )}
        </button>
      </div>

      {/* Address Display */}
      {address && (
        <div style={{
          position: 'absolute',
          bottom: '16px',
          left: '16px',
          right: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          zIndex: 100
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
            Detected Address:
          </div>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {address}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleMapPicker;
